import {Capacitor} from "@capacitor/core";
import {createContext, useContext, useEffect, useMemo, useReducer} from "react";
import api, {apiAction, apiCall} from "./api";
import {config} from "./config/config.ts";
import {Player} from "./types";
import {claimQuizResponses} from "./utilities/quizClaim.ts";

const ACTIONS = {
    setToken:   "setToken",
    clearToken: "clearToken",
    setUser:    "setUser",
};

const authReducer = (state: any, action: any) => {
    switch (action.type) {
        case ACTIONS.setToken:
            return {...state, jwt_token: action.payload};

        case ACTIONS.clearToken:
            return {...state, jwt_token: null};

        case ACTIONS.setUser:
            return {...state, user: action.payload};

        default:
            console.error(`You passed an action.type: ${action.type} which doesn't exist`);
    }
};

if (!config.isCordova && (typeof sessionStorage !== 'undefined') && !sessionStorage.landingUrl) {
    sessionStorage.landingUrl = document.location.href;
}

const initialData: {
    jwt_token: string,
    user?: null | Player // undefined - loading, null - unauthenticated // false causes typing issues
    initialized: boolean,
    unauthenticated: boolean,
    landingUrl: string,

    setToken: (_) => void,
    clearToken: () => void,
    setFieldsFromResponse: (_) => void,
    /** Replace the in-memory user (e.g. an optimistic update); reloadUser reconciles from the API. */
    setUser: (_: Player) => void,
    reloadUser: () => Promise<any>,
    signInWithEmailAndPassword: (_u: string, _p: string) => Promise<any>,
    signOut: () => void,
    makeSureUserExists: (_u: string, _p: string, _uid: string) => void,
} = {
    jwt_token:  typeof localStorage !== 'undefined' && localStorage.getItem("jwt_token"),
    landingUrl: (((typeof sessionStorage !== 'undefined') && sessionStorage?.landingUrl) || null),
} as any;

const AuthContext = createContext(initialData);

const JWTAuthProvider = ({children}) => {
    const [state, dispatch] = useReducer(authReducer, initialData);

    const setToken = (newToken: string | null) => {
        api.jwt_token = newToken || '';
        typeof localStorage !== 'undefined' && localStorage.setItem("jwt_token", newToken || '');

        dispatch({type: ACTIONS.setToken, payload: newToken});
    };
    const setUser = (newUser: string | null | false) => {
        api.user = newUser;
        if (api.user?.uid) {
            if (api.user?.is_subscribed || api.user?.meta?.disable_ads) {
                (typeof localStorage !== 'undefined') && (localStorage.disable_ads = 'y');
            } else {
                typeof localStorage !== 'undefined' && localStorage.removeItem('disable_ads');
            }
        }
        if (config.clarityProjectId && api.user?.email && Capacitor.getPlatform() !== 'ios') {
            // Blocked or not-yet-loaded Clarity leaves window.clarity undefined; identify then throws.
            import("@microsoft/clarity")
                .then((Clarity) => { if (typeof (window as any).clarity === 'function') Clarity.default.identify(api.user?.email); })
                .catch(() => {});
        }
        // Signed in: link any quiz this browser took before the account existed (Google, hand-offs).
        if (api.user?.uid) claimQuizResponses();
        dispatch({type: ACTIONS.setUser, payload: newUser});
    };
    const clearToken = () => {
        api.jwt_token = '';
        typeof localStorage !== 'undefined' && localStorage.removeItem("jwt_token");

        dispatch({type: ACTIONS.clearToken});
    };
    const setFieldsFromResponse = (res) => {
        if (res.access_token) {
            setToken(res.access_token);
            if (res.user) {
                setUser(res.user);
            }
            followWithMe();
        }
    };
    /**
     * A sign-in response carries the user, but not everything auth/me adds (the feature switches,
     * the football club, ...), and nothing else asks auth/me again until the next page load. So
     * every fresh token is followed by auth/me, and screens that wait on those fields get them
     * without a reload.
     */
    const followWithMe = () => {
        reloadUser().catch((e) => console.debug('auth/me after sign-in failed', e));
    };
    const reloadUser = async () => {
        return apiCall('auth/me')
            .then(async (res) => res.json())
            .then((res: any) => {
                if (res?.email) {
                    console.debug('auth/me/reload:', res);
                    setUser(res);
                }
                return res;
            })
    };

    const signInWithEmailAndPassword = async (email: string, password: string) => {
        const res = await apiAction('auth/login', {email, password});
        if (res.access_token) {
            setToken(res.access_token);
            if (res.user) {
                setUser(res.user);
            }
            followWithMe();
            return {user: res.user};
        }
        return {};
    };
    const signOut = async () => {
        await apiAction('auth/logout');
        setToken('');
        setUser(null);
        // A master's saved session from "Sign in as" must not outlive a sign-out.
        typeof localStorage !== 'undefined' && localStorage.removeItem('impersonator_token');
        typeof sessionStorage !== 'undefined' && sessionStorage.clear(); // clears sessionStorage.trial_ended_seen
        (typeof window !== 'undefined') && window && (window['adsbygoogle'] = []);
    };
    const makeSureUserExists = async (email: string, password: string, uid: string) => {
        let res = await apiAction('auth/makeSureUserExists', {email, password, uid});
        if (res.user) {
            setUser(res.user);
        }
    };

    useEffect(() => {
        ;(async () => {
            if (!state.jwt_token) {
                setUser(null);
                return;
            }
            api.user = {};
            api.jwt_token = state.jwt_token;
            apiCall('auth/me')
                .then(async (res) => {
                    console.debug('res.status', res.status);
                    if (res.status == 401) {
                        console.info('JWT Token has expired.')
                        setToken('');
                        setUser(null);
                        return null;
                    }
                    return res.json()
                })
                .then((res: any) => {
                    if (res?.email) {
                        console.debug('auth/me:', res);
                        setUser(res);
                    }
                });
        })();

        const interval = setInterval(() => {
            // Read the live api.jwt_token, NOT state.jwt_token: this effect has an empty dependency
            // list, so state is captured from the first render. A token acquired later in the same
            // page session -- claiming an invite, logging in -- left the captured value empty, the
            // guard below returned every tick, and the token was never refreshed. It then simply
            // expired at JWT_TTL (60m) mid-session and stranded the user on 401s.
            if (!api.jwt_token) {
                return;
            }
            apiCall('auth/refresh')
                .then(async (res) => {
                    // A definite 401 means the session is past its refresh window: clear the dead
                    // token so the app lands on login, instead of keeping it and stranding the
                    // user on 401s everywhere. Network errors deliberately do NOT log anyone out.
                    if (res.status === 401) {
                        clearToken();
                        return null;
                    }
                    return res.json();
                }, (err) => console.log('err', err))
                .then((res) => {
                    if (res?.access_token) {
                        setToken(res.access_token);
                    }
                });
        }, config.jwtRefreshSeconds * 1000);

        return () => {
            clearInterval(interval);
        };
    }, []);

    const contextValue = useMemo(
        () => ({
            ...state,
            initialized:     (state.user !== undefined),
            unauthenticated: (state.user === null),
            setToken,
            clearToken,
            setUser,
            signInWithEmailAndPassword,
            reloadUser,
            signOut,
            makeSureUserExists,
            setFieldsFromResponse,
        }),
        [state]
    );

    return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export const useJwtAuth = () => {
    return useContext(AuthContext);
};

export const useJwtUser = () => {
    return useContext(AuthContext).user;
};

export default JWTAuthProvider;
